// src/services/socialAuth.ts
// Google + Apple sosyal giriş — provider akışı, popup→redirect fallback,
// ilk-giriş tespiti ve ilk-giriş profil oluşturma (registerSocialUser callable).
//
// OWNER NOTU: Google çalışması için Firebase Console → Authentication →
// Sign-in method → Google "Enable" gerekir. Apple ayrıca Apple Developer
// (Services ID + key) config'i ister → SOCIAL_APPLE_ENABLED flag'i ile gizli.

import { auth } from "../firebase";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

// Apple, owner config'i tamamlanana kadar gizli. Aktive etmek için bunu
// true yap (veya localStorage 'mylasa.social.apple'='on').
export const SOCIAL_APPLE_ENABLED =
  (typeof window !== "undefined" &&
    window.localStorage?.getItem("mylasa.social.apple") === "on") ||
  false;

export interface SocialResult {
  user: any | null;
  isNewUser: boolean;
  pendingRedirect?: boolean;
}

// iOS Safari ve ÖZELLİKLE standalone PWA (ana ekrana eklenmiş) signInWithPopup'ı
// pratikte desteklemez: popup açılır ama oturum ana sekmeye dönmez → kullanıcı
// hiç giriş yapamaz, üstelik yakalanabilir bir hata da gelmeyebilir. Bu yüzden
// iOS'ta popup'ı HİÇ denemeyip doğrudan redirect kullanırız (en güvenli).
function isIosStandaloneOrSafari(): boolean {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) ||
      // iPadOS 13+ masaüstü UA verir; dokunmatik Mac ayrımı:
      (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
    if (!isIos) return false;
    const standalone =
      (navigator as any).standalone === true ||
      (typeof window !== "undefined" &&
        window.matchMedia?.("(display-mode: standalone)")?.matches === true);
    // Standalone'da KESİN redirect; normal iOS Safari'de de popup kırılgan →
    // iOS'un tamamında redirect tercih ediyoruz (Firebase önerisi).
    return isIos || standalone;
  } catch {
    return false;
  }
}

// Popup desteklenmiyorsa (bazı mobil PWA / in-app webview) redirect'e düş.
function shouldFallbackToRedirect(code: string): boolean {
  return (
    code === "auth/popup-blocked" ||
    code === "auth/operation-not-supported-in-this-environment" ||
    code === "auth/cancelled-popup-request"
  );
}

async function signInWith(provider: GoogleAuthProvider | OAuthProvider): Promise<SocialResult> {
  // iOS → popup'ı hiç deneme, doğrudan redirect (popup sessiz kapanma fix'i).
  if (isIosStandaloneOrSafari()) {
    await signInWithRedirect(auth, provider);
    return { user: null, isNewUser: false, pendingRedirect: true };
  }
  try {
    const cred = await signInWithPopup(auth, provider);
    const info = getAdditionalUserInfo(cred);
    return { user: cred.user, isNewUser: !!info?.isNewUser };
  } catch (e: any) {
    const code = String(e?.code || "");
    if (shouldFallbackToRedirect(code)) {
      // Redirect: sayfa yeniden yüklenir; sonuç resolveRedirectResult() ile alınır.
      await signInWithRedirect(auth, provider);
      return { user: null, isNewUser: false, pendingRedirect: true };
    }
    throw e; // popup-closed-by-user / account-exists vb. çağırana bırakılır.
  }
}

export function signInWithGoogle(): Promise<SocialResult> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWith(provider);
}

export function signInWithApple(): Promise<SocialResult> {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return signInWith(provider);
}

// Redirect dönüşünde çağrılır (Auth mount). Sonuç yoksa null.
//
// BUG FIX (2026-06-30): Önceden burada hata sessizce yutulup null dönülüyordu
// ("auth/account-exists-with-different-credential", "auth/unauthorized-domain"
// gibi GERÇEK/aksiyon-alınabilir hatalar dahil) — getRedirectResult() bekleyen
// bir redirect YOKSA zaten null döner (hata fırlatmaz), o yüzden buradaki catch
// SADECE gerçek bir önceki redirect denemesinin hatasını yakalıyordu ve bu hata
// hiçbir yere gösterilmiyordu. iOS'ta TÜM Google girişleri redirect kullandığı
// için (bkz. isIosStandaloneOrSafari) bu, iOS kullanıcılarının "giriş var ama
// girilmiyor" diye hissettiği sessiz başarısızlığın kök nedeniydi — masaüstü
// popup yolunda aynı hata Auth.tsx handleGoogle catch'inde düzgün gösteriliyordu
// (asimetri). Artık hata çağırana fırlatılır; Auth.tsx bunu yakalayıp kullanıcıya
// gösterir (authErrorMessage ile Türkçe mesaja çevrilir).
export async function resolveRedirectResult(): Promise<SocialResult | null> {
  const cred = await getRedirectResult(auth);
  if (!cred) return null;
  const info = getAdditionalUserInfo(cred);
  return { user: cred.user, isNewUser: !!info?.isNewUser };
}

// Google displayName / email'den geçerli username önerisi türetir.
export function suggestUsername(seed: string): string {
  const base = String(seed || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // aksanları kaldır
    .replace(/[^a-z0-9._]/g, "")
    .replace(/^[._]+|[._]+$/g, "")
    .slice(0, 20);
  return base || "kullanici";
}

// İlk-giriş profil oluşturma — registerSocialUser callable (idempotent).
//
// 🔴 `birthday` ZORUNLU (bu turda eklendi). Öncesinde bu fonksiyon doğum
// tarihi TOPLAMIYORDU ve `registerSocialUser` içinde `birthday` sözcüğü hiç
// geçmiyordu: Google/Apple ile kaydolan kullanıcı için 13 yaş kuralı NE
// İSTEMCİDE NE SUNUCUDA vardı. Bu aynı zamanda bir BEYAN sorunuydu —
// `src/pages/legal/legalContent.ts` kullanıcıya "Yaş doğrulama: doğum tarihi
// (yalnızca 13+ yaş kontrolü için)" diyor.
// Sunucu artık FAIL-CLOSED: alan eksik/biçimsizse çağrı `invalid-argument`
// ile REDDEDİLİR. Bu yüzden tip de opsiyonel DEĞİL — eksikliği derleme
// zamanında yakalansın.
export async function completeSocialSignup(params: {
  username: string;
  birthday: string; // "YYYY-MM-DD" — sunucu doğrular (fail-closed)
  displayName?: string;
  photoURL?: string;
}): Promise<{ uid: string }> {
  const fns = getFunctions(undefined, "europe-west3");
  const call = httpsCallable(fns, "registerSocialUser");
  const res = await call({
    username: params.username,
    birthday: params.birthday,
    displayName: params.displayName || "",
    photoURL: params.photoURL || "",
  });
  return res.data as { uid: string };
}
